import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { QueryVal } from 'Helpers/Query';
import { getAPIURL } from 'Consts/sharedUris';

const HOME_LINK_QUERY_PARAM = 'homeLink';

export const HomeContext = React.createContext(null);

const getMSSinceEpoch = () => {
  return new Date().valueOf();
};

const RECENT_FETCHED_THRESHOLD_MS = 50;

const fetchToFacetRestResponse = async (fetchPromise) => {
  const res = await fetchPromise;
  const json = await res.json();
  return fr.response(json);
};

export function HomeContextProvider({ children }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [homeResource, setHomeResource] = useState(null);
  // We need the home resource to also be available as a ref so that refreshHomeResource can use the latest value - ditto lastFetchedAt
  const homeResourceRef = useRef(null);
  const lastFetchedAt = useRef(null);

  const fetchHomeResource = React.useCallback(async () => {
    const apiUrl = getAPIURL();
    if (apiUrl === undefined) {
      throw new Error('API_URL missing from environment');
    }

    const browserURL = new URL(window.location.href);
    let homeLink = QueryVal(HOME_LINK_QUERY_PARAM, '');
    if (!homeLink.startsWith(apiUrl)) {
      homeLink = `${apiUrl}/`;
    }
    homeLink = new URL(homeLink);
    homeLink.searchParams.set('pageUrl', browserURL.href);
    const [homeRes] = await fr.http.get(homeLink.toString());
    lastFetchedAt.current = getMSSinceEpoch();
    homeResourceRef.current = homeRes;
    setHomeResource(homeRes);
    return homeRes;
  }, []);

  const initializeHomeResource = async () => {
    if (!window.facetHomeEndpointInitialFetch) {
      await fetchHomeResource();
      setIsInitializing(false);
      return;
    }
    // We've already started fetching the home resource, let's use that request
    const [homeRes] = await fetchToFacetRestResponse(window.facetHomeEndpointInitialFetch);
    lastFetchedAt.current = getMSSinceEpoch();
    homeResourceRef.current = homeRes;
    setHomeResource(homeRes);
    setIsInitializing(false);
    // Remove the initial fetch data, so that if the home context ever gets re-mounted we do a re-fetch - otherwise we could sometimes have stale data issues
    window.facetHomeEndpointInitialFetch = undefined;
  };

  const refreshHomeResource = React.useCallback((args = {}) => {
    const { skipFetchIfRecentlyUpdated = false } = args;

    const isRecentlyUpdated = () => {
      if (!lastFetchedAt.current) {
        return false;
      }

      return getMSSinceEpoch() - lastFetchedAt.current < RECENT_FETCHED_THRESHOLD_MS;
    };

    if (skipFetchIfRecentlyUpdated && isRecentlyUpdated()) {
      return homeResourceRef.current;
    }

    return fetchHomeResource();
  }, []);


  useComponentDidMount(async () => {
    try {
      await initializeHomeResource();
    } catch (err) {
      setIsInitializing(false);
    }
  });

  const memoizedValue = React.useMemo(() => {
    return {
      homeResource,
      isInitializing,
      refreshHomeResource,
      isDebugUser: homeResource?.withRelationship(fr.relationships.debugAccess.name, () => true, () => false),
      hasLegacyDashboardImprovement: homeResource?.withRelationship(fr.relationships.legacyDashboardImprovement.name, () => true, () => false),
    };
  }, [
    isInitializing,
    homeResource,
    refreshHomeResource,
  ]);

  return (
    <HomeContext.Provider
      value={memoizedValue}
    >
      {children}
    </HomeContext.Provider>
  );
}

HomeContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

function useHomeContext() {
  const context = React.useContext(HomeContext);
  if (!context) throw new Error('useHome must be called from a HomeContext.Provider');
  return context;
}

export default useHomeContext;
